import { useState, useCallback, useEffect, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getTemplates,
  getTemplate,
  createTemplate,
  uploadTemplate,
  updateTemplate,
  deleteTemplate,
  downloadTemplate,
  publishTemplate,
  getTemplateBlob,
  startAiifyJob,
  getAiifyJobStatus,
  downloadAiifyResult,
  AiifyJobResponse,
  Template,
  TemplateListItem,
  UseCase,
  USE_CASE_OPTIONS,
} from '../services/api'
import { getClient } from '../lib/aperture'
import DocxPreview from '../components/DocxPreview'

const glassPanel = {
  background: 'rgba(15, 23, 42, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(100, 180, 255, 0.1)',
}

const modalBg = {
  background: 'rgba(15, 23, 42, 0.95)',
  border: '1px solid rgba(100, 180, 255, 0.2)',
}

export default function Templates() {
  const queryClient = useQueryClient()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showAiifyModal, setShowAiifyModal] = useState(false)
  const [aiifyProcessing, setAiifyProcessing] = useState(false)
  const [aiifyJob, setAiifyJob] = useState<AiifyJobResponse | null>(null)
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null)
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [useCaseFilter, setUseCaseFilter] = useState('all')
  const [includeInactive, setIncludeInactive] = useState(false)

  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    content: '',
    category: 'general',
    use_case: 'other' as UseCase,
  })

  const [uploadMode, setUploadMode] = useState<'form' | 'file'>('file')
  const [dragActive, setDragActive] = useState(false)
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)

  const { data: templates, isLoading } = useQuery({
    queryKey: ['templates', includeInactive],
    queryFn: () => getTemplates(includeInactive).then((res) => res.data),
  })

  const createMutation = useMutation({
    mutationFn: createTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
      setShowCreateModal(false)
      resetForm()
    },
  })

  const uploadMutation = useMutation({
    mutationFn: uploadTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
      setShowCreateModal(false)
      resetForm()
    },
    onError: (error: Error & { response?: { data?: { detail?: string } } }) => {
      const message = error.response?.data?.detail || error.message || 'Upload failed'
      alert(`Upload failed: ${message}`)
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Template> }) => updateTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
      setShowEditModal(false)
      setSelectedTemplate(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['templates'] })
    },
  })

  const publishMutation = useMutation({
    mutationFn: (id: string) => publishTemplate(id),
    onSuccess: () => {
      getClient()?.capture('output_metric', { metric_key: 'templates_published', value: 1 })
      queryClient.invalidateQueries({ queryKey: ['templates'] })
    },
  })

  const resetForm = () => {
    setFormData({ name: '', display_name: '', description: '', content: '', category: 'general', use_case: 'other' })
  }

  const handleCreateSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    createMutation.mutate(formData)
  }

  const handleFileUpload = async (file: File) => {
    const isMarkdown = file.name.endsWith('.md')
    const isDocx = file.name.endsWith('.docx')
    if (!isMarkdown && !isDocx) {
      alert('Please upload a Markdown (.md) or Word (.docx) file')
      return
    }
    const ext = isDocx ? '.docx' : '.md'
    const baseName = file.name.replace(ext, '')
    const formDataObj = new FormData()
    formDataObj.append('file', file)
    formDataObj.append('name', formData.name || baseName.toLowerCase().replace(/\s+/g, '-'))
    formDataObj.append('display_name', formData.display_name || baseName)
    formDataObj.append('description', formData.description)
    formDataObj.append('category', formData.category)
    formDataObj.append('use_case', formData.use_case)
    uploadMutation.mutate(formDataObj)
  }

  const handleDownloadTemplate = async (template: TemplateListItem) => {
    try {
      await downloadTemplate(template.id, `${template.name}.docx`)
    } catch (error) {
      console.error('Download failed:', error)
      alert('Failed to download template')
    }
  }

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current)
      }
    }
  }, [])

  const handleAiifyUpload = async (file: File) => {
    if (!file.name.endsWith('.docx') && !file.name.endsWith('.dotx')) {
      alert('Please upload a Word (.docx or .dotx) file')
      return
    }
    setAiifyProcessing(true)
    setAiifyJob(null)

    try {
      // Start the job
      const job = await startAiifyJob(file)
      setAiifyJob(job)

      // Start polling for status
      pollingRef.current = setInterval(async () => {
        try {
          const status = await getAiifyJobStatus(job.id)
          setAiifyJob(status)

          if (status.status === 'completed') {
            // Stop polling
            if (pollingRef.current) {
              clearInterval(pollingRef.current)
              pollingRef.current = null
            }
            setAiifyProcessing(false)

            // Download the result
            const originalName = file.name.replace(/\.(docx|dotx)$/i, '')
            await downloadAiifyResult(job.id, `${originalName}-ai-template.docx`)
            getClient()?.capture('output_metric', { metric_key: 'templates_aiified', value: 1 })
            setShowAiifyModal(false)
            setAiifyJob(null)
          } else if (status.status === 'error') {
            // Stop polling
            if (pollingRef.current) {
              clearInterval(pollingRef.current)
              pollingRef.current = null
            }
            setAiifyProcessing(false)
            alert(`Conversion failed: ${status.error_message || 'Unknown error'}`)
          }
        } catch (pollError) {
          console.error('Polling error:', pollError)
        }
      }, 2000) // Poll every 2 seconds

    } catch (error: unknown) {
      console.error('AI-ify failed:', error)
      const errorMessage = error instanceof Error
        ? error.message
        : 'Failed to start conversion. Please try again.'
      alert(errorMessage)
      setAiifyProcessing(false)
      setAiifyJob(null)
    }
  }

  const handleCancelAiify = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current)
      pollingRef.current = null
    }
    setAiifyProcessing(false)
    setAiifyJob(null)
    setShowAiifyModal(false)
  }

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true)
    else if (e.type === 'dragleave') setDragActive(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)
    if (e.dataTransfer.files && e.dataTransfer.files[0]) handleFileUpload(e.dataTransfer.files[0])
  }, [formData])

  const handleViewTemplate = async (template: TemplateListItem) => {
    const { data } = await getTemplate(template.id)
    setSelectedTemplate(data)
    setPreviewBlob(null)
    setShowViewModal(true)

    // Fetch blob for DOCX preview
    if (template.file_type === 'docx') {
      setPreviewLoading(true)
      try {
        const blob = await getTemplateBlob(template.id)
        setPreviewBlob(blob)
      } catch (error) {
        console.error('Failed to load preview:', error)
      } finally {
        setPreviewLoading(false)
      }
    }
  }

  const handleEditTemplate = async (template: TemplateListItem) => {
    const { data } = await getTemplate(template.id)
    setSelectedTemplate(data)
    setShowEditModal(true)
  }

  const handleDelete = (template: TemplateListItem) => {
    if (confirm(`Delete template "${template.display_name}"? This cannot be undone.`)) {
      deleteMutation.mutate(template.id)
    }
  }

  const handleUpdateSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedTemplate) return
    updateMutation.mutate({
      id: selectedTemplate.id,
      data: {
        display_name: selectedTemplate.display_name,
        description: selectedTemplate.description,
        content: selectedTemplate.content,
        category: selectedTemplate.category,
        use_case: selectedTemplate.use_case,
        is_active: selectedTemplate.is_active,
      },
    })
  }

  // Filter out OG templates (category === 'og-template') - those show on the Template Inbox page
  const templateList = Array.isArray(templates) ? templates.filter((t) => t.category !== 'og-template') : []
  const categories = [...new Set(templateList.map((t) => t.category))].sort()
  const filteredTemplates = templateList.filter((t) => {
    const matchesSearch = t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.display_name.toLowerCase().includes(search.toLowerCase()) ||
      t.description.toLowerCase().includes(search.toLowerCase())
    const matchesCategory = categoryFilter === 'all' || t.category === categoryFilter
    const matchesUseCase = useCaseFilter === 'all' || t.use_case === useCaseFilter
    return matchesSearch && matchesCategory && matchesUseCase
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(139, 92, 246, 0.1)', border: '2px solid rgba(139, 92, 246, 0.3)', boxShadow: '0 0 30px rgba(139, 92, 246, 0.3)' }}>
            <i className="fa-solid fa-spinner fa-spin text-2xl" style={{ color: '#a78bfa' }}></i>
          </div>
          <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Loading templates...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>Templates</h1>
          <p className="text-sm mt-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Manage document generation templates</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search templates..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-80 pl-10 pr-4 py-2.5 rounded-xl text-sm"
              style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }}
            />
            <i className="fa-solid fa-search absolute left-3 top-3" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
          </div>
          <button
            onClick={() => setShowAiifyModal(true)}
            className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2 hover:scale-105"
            style={{ background: 'rgba(139, 92, 246, 0.2)', border: '1px solid rgba(139, 92, 246, 0.4)', color: '#a78bfa' }}
          >
            <i className="fa-solid fa-wand-magic-sparkles"></i>
            <span>AI-ify</span>
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2 hover:scale-105"
            style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)', border: '1px solid rgba(96, 165, 250, 0.4)', color: '#e2e8f0' }}
          >
            <i className="fa-solid fa-plus"></i>
            <span>New Template</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02]" style={{ ...glassPanel, borderColor: 'rgba(59, 130, 246, 0.2)' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(59, 130, 246, 0.15)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
              <i className="fa-solid fa-file-code text-xl" style={{ color: '#60a5fa' }}></i>
            </div>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Total Templates</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{templateList.length}</p>
        </div>

        <div className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02]" style={{ ...glassPanel, borderColor: 'rgba(52, 211, 153, 0.2)' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(52, 211, 153, 0.15)', border: '1px solid rgba(52, 211, 153, 0.3)' }}>
              <i className="fa-solid fa-check-circle text-xl" style={{ color: '#34d399' }}></i>
            </div>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Active Templates</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{templateList.filter((t) => t.is_active).length}</p>
        </div>

        <div className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02]" style={{ ...glassPanel, borderColor: 'rgba(139, 92, 246, 0.2)' }}>
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(139, 92, 246, 0.15)', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
              <i className="fa-solid fa-tags text-xl" style={{ color: '#a78bfa' }}></i>
            </div>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Categories</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{categories.length}</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="rounded-xl p-4" style={glassPanel}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Filter by:</span>
            <select
              value={useCaseFilter}
              onChange={(e) => setUseCaseFilter(e.target.value)}
              className="px-4 py-2 rounded-lg text-sm"
              style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }}
            >
              <option value="all">All Use Cases</option>
              <option value="aidw">AIDW</option>
              <option value="caf">CAF</option>
              <option value="waf">WAF</option>
              <option value="other">Other</option>
            </select>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2 rounded-lg text-sm"
              style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }}
            >
              <option value="all">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
            <label className="flex items-center space-x-2 text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                className="rounded"
                style={{ background: 'rgba(30, 41, 59, 0.5)', borderColor: 'rgba(100, 180, 255, 0.3)' }}
              />
              <span>Show inactive</span>
            </label>
          </div>
        </div>
      </div>

      {/* Templates Table */}
      {filteredTemplates.length > 0 ? (
        <div className="rounded-xl overflow-hidden" style={glassPanel}>
          <table className="w-full">
            <thead style={{ borderBottom: '1px solid rgba(100, 180, 255, 0.1)' }}>
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>Template</th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>Type</th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>Use Case</th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>Category</th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>Status</th>
                <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>Updated</th>
                <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTemplates.map((template, index) => (
                <tr key={template.id} style={{ borderBottom: index < filteredTemplates.length - 1 ? '1px solid rgba(100, 180, 255, 0.05)' : 'none' }}>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium" style={{ color: '#e2e8f0' }}>{template.display_name}</p>
                      <p className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>{template.name}</p>
                      {template.description && <p className="text-xs mt-1 line-clamp-1" style={{ color: 'rgba(148, 163, 184, 0.4)' }}>{template.description}</p>}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {template.file_type === 'docx' ? (
                      <span className="px-2.5 py-1 text-xs font-medium rounded-full inline-flex items-center"
                        style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                        <i className="fa-solid fa-file-word mr-1"></i>Word
                      </span>
                    ) : (
                      <span className="px-2.5 py-1 text-xs font-medium rounded-full inline-flex items-center"
                        style={{ background: 'rgba(148, 163, 184, 0.15)', color: '#94a3b8', border: '1px solid rgba(148, 163, 184, 0.3)' }}>
                        <i className="fa-solid fa-file-code mr-1"></i>Markdown
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2.5 py-1 text-xs font-medium rounded-full uppercase"
                      style={{
                        background: template.use_case === 'aidw' ? 'rgba(251, 191, 36, 0.15)' :
                                   template.use_case === 'caf' ? 'rgba(59, 130, 246, 0.15)' :
                                   template.use_case === 'waf' ? 'rgba(52, 211, 153, 0.15)' :
                                   'rgba(148, 163, 184, 0.15)',
                        color: template.use_case === 'aidw' ? '#fbbf24' :
                               template.use_case === 'caf' ? '#60a5fa' :
                               template.use_case === 'waf' ? '#34d399' :
                               '#94a3b8',
                        border: `1px solid ${template.use_case === 'aidw' ? 'rgba(251, 191, 36, 0.3)' :
                                            template.use_case === 'caf' ? 'rgba(59, 130, 246, 0.3)' :
                                            template.use_case === 'waf' ? 'rgba(52, 211, 153, 0.3)' :
                                            'rgba(148, 163, 184, 0.3)'}`
                      }}>
                      {template.use_case}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="px-2.5 py-1 text-xs font-medium rounded-full"
                      style={{ background: 'rgba(139, 92, 246, 0.15)', color: '#a78bfa', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
                      {template.category}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {template.status === 'draft' ? (
                      <span className="px-2.5 py-1 text-xs font-medium rounded-full"
                        style={{ background: 'rgba(251, 191, 36, 0.15)', color: '#fbbf24', border: '1px solid rgba(251, 191, 36, 0.3)' }}>
                        <i className="fa-solid fa-pen-to-square mr-1"></i>Draft
                      </span>
                    ) : (
                      <span className="px-2.5 py-1 text-xs font-medium rounded-full"
                        style={{ background: 'rgba(52, 211, 153, 0.15)', color: '#34d399', border: '1px solid rgba(52, 211, 153, 0.3)' }}>
                        v{template.version || 1}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                    {new Date(template.updated_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end space-x-2">
                      {template.status === 'draft' && (
                        <button
                          onClick={() => {
                            if (confirm('Publish this template? It will be available for document generation.')) {
                              publishMutation.mutate(template.id)
                            }
                          }}
                          className="p-2 rounded-lg transition hover:scale-110"
                          style={{ color: '#34d399' }}
                          title="Publish Template"
                        >
                          <i className="fa-solid fa-check-circle"></i>
                        </button>
                      )}
                      {template.file_type === 'docx' && (
                        <button onClick={() => handleDownloadTemplate(template)} className="p-2 rounded-lg transition" style={{ color: 'rgba(148, 163, 184, 0.6)' }} title="Download">
                          <i className="fa-solid fa-download"></i>
                        </button>
                      )}
                      <button onClick={() => handleViewTemplate(template)} className="p-2 rounded-lg transition" style={{ color: 'rgba(148, 163, 184, 0.6)' }} title="View">
                        <i className="fa-solid fa-eye"></i>
                      </button>
                      <button onClick={() => handleEditTemplate(template)} className="p-2 rounded-lg transition" style={{ color: 'rgba(148, 163, 184, 0.6)' }} title="Edit">
                        <i className="fa-solid fa-edit"></i>
                      </button>
                      <button onClick={() => handleDelete(template)} className="p-2 rounded-lg transition" style={{ color: 'rgba(148, 163, 184, 0.6)' }} title="Delete">
                        <i className="fa-solid fa-trash"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rounded-xl p-12 text-center" style={glassPanel}>
          <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
            style={{ background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.2)' }}>
            <i className="fa-solid fa-file-code text-3xl" style={{ color: 'rgba(167, 139, 250, 0.5)' }}></i>
          </div>
          <h3 className="text-lg font-semibold mb-2" style={{ color: '#e2e8f0' }}>
            {search || categoryFilter !== 'all' ? 'No templates match your filters' : 'No templates yet'}
          </h3>
          <p className="mb-6" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
            {search || categoryFilter !== 'all' ? 'Try adjusting your search or filter criteria' : 'Upload a Markdown or Word template to get started'}
          </p>
          {!search && categoryFilter === 'all' && (
            <button onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 rounded-xl text-sm font-medium transition-all duration-200 inline-flex items-center space-x-2 hover:scale-105"
              style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)', border: '1px solid rgba(96, 165, 250, 0.4)', color: '#e2e8f0' }}>
              <i className="fa-solid fa-plus"></i>
              <span>Create your first template</span>
            </button>
          )}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(8px)' }}
          onClick={(e) => e.target === e.currentTarget && setShowCreateModal(false)}>
          <div className="rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" style={modalBg}>
            <div className="sticky top-0 px-8 py-6 flex items-center justify-between" style={{ ...modalBg, borderBottom: '1px solid rgba(100, 180, 255, 0.1)' }}>
              <div>
                <h3 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>Create New Template</h3>
                <p className="text-sm mt-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Upload a template file</p>
              </div>
              <button onClick={() => setShowCreateModal(false)} style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                <i className="fa-solid fa-times text-2xl"></i>
              </button>
            </div>

            <div className="px-8 py-6">
              <div className="flex space-x-2 mb-6">
                <button onClick={() => setUploadMode('file')}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition"
                  style={{ background: uploadMode === 'file' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(30, 41, 59, 0.5)', color: uploadMode === 'file' ? '#60a5fa' : 'rgba(148, 163, 184, 0.7)', border: '1px solid ' + (uploadMode === 'file' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(100, 180, 255, 0.1)') }}>
                  <i className="fa-solid fa-upload mr-2"></i>Upload File
                </button>
                <button onClick={() => setUploadMode('form')}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition"
                  style={{ background: uploadMode === 'form' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(30, 41, 59, 0.5)', color: uploadMode === 'form' ? '#60a5fa' : 'rgba(148, 163, 184, 0.7)', border: '1px solid ' + (uploadMode === 'form' ? 'rgba(59, 130, 246, 0.3)' : 'rgba(100, 180, 255, 0.1)') }}>
                  <i className="fa-solid fa-edit mr-2"></i>Write Content
                </button>
              </div>

              <form onSubmit={uploadMode === 'form' ? handleCreateSubmit : (e) => e.preventDefault()}>
                <div className="space-y-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                        Template Name (slug) <span style={{ color: '#f87171' }}>*</span>
                      </label>
                      <input type="text" placeholder="assessment-template" required value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                        className="w-full px-4 py-3 rounded-xl text-sm"
                        style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }} />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                        Display Name <span style={{ color: '#f87171' }}>*</span>
                      </label>
                      <input type="text" placeholder="Assessment Template" required value={formData.display_name}
                        onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl text-sm"
                        style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }} />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>Description</label>
                    <input type="text" placeholder="Brief description of this template" value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl text-sm"
                      style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }} />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>Use Case</label>
                      <select value={formData.use_case} onChange={(e) => setFormData({ ...formData, use_case: e.target.value as UseCase })}
                        className="w-full px-4 py-3 rounded-xl text-sm"
                        style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }}>
                        {['Frameworks', 'Technology', 'Document Types', 'Services', 'Other'].map((group) => (
                          <optgroup key={group} label={group}>
                            {USE_CASE_OPTIONS.filter((o) => o.group === group).map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>Category</label>
                      <select value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl text-sm"
                        style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }}>
                        <option value="general">General</option>
                        <option value="workshop">Workshop</option>
                        <option value="assessment">Assessment</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  {uploadMode === 'file' ? (
                    <div onDragEnter={handleDrag} onDragLeave={handleDrag} onDragOver={handleDrag} onDrop={handleDrop}
                      className="rounded-xl p-8 text-center transition"
                      style={{ border: `2px dashed ${dragActive ? 'rgba(59, 130, 246, 0.5)' : 'rgba(100, 180, 255, 0.2)'}`, background: dragActive ? 'rgba(59, 130, 246, 0.1)' : 'transparent' }}>
                      <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
                        style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                        <i className="fa-solid fa-file-upload text-3xl" style={{ color: 'rgba(96, 165, 250, 0.5)' }}></i>
                      </div>
                      <p className="mb-2" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Drag and drop your template file here</p>
                      <p className="text-sm mb-4" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>or</p>
                      <label className="px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition hover:scale-105"
                        style={{ background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.4)', color: '#60a5fa' }}>
                        Browse Files
                        <input type="file" accept=".md,.docx" className="hidden"
                          onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0])} />
                      </label>
                      <p className="text-xs mt-4" style={{ color: 'rgba(148, 163, 184, 0.4)' }}>
                        Supported formats: .md (Markdown) or .docx (Word with [[prompt]] placeholders)
                      </p>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>
                        Template Content (Markdown) <span style={{ color: '#f87171' }}>*</span>
                      </label>
                      <textarea rows={12} placeholder="# Template Title&#10;&#10;## Section 1&#10;<!-- AI_PROMPT: Instructions for AI to generate this section -->"
                        required value={formData.content} onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl text-sm font-mono resize-none"
                        style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }}></textarea>
                    </div>
                  )}

                  <div className="rounded-xl p-4" style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                    <div className="flex space-x-3">
                      <i className="fa-solid fa-info-circle mt-0.5" style={{ color: '#60a5fa' }}></i>
                      <div>
                        <h4 className="text-sm font-semibold mb-1" style={{ color: '#60a5fa' }}>Template Formats</h4>
                        <p className="text-sm mb-2" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                          <strong>Markdown (.md):</strong> Use &lt;!-- AI_PROMPT: ... --&gt; comments for AI generation.
                        </p>
                        <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                          <strong>Word (.docx):</strong> Use [[prompt instructions]] placeholders.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3 mt-8 pt-6" style={{ borderTop: '1px solid rgba(100, 180, 255, 0.1)' }}>
                  <button type="button" onClick={() => setShowCreateModal(false)}
                    className="px-6 py-3 rounded-xl text-sm font-medium transition"
                    style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: 'rgba(148, 163, 184, 0.9)' }}>
                    Cancel
                  </button>
                  {uploadMode === 'form' && (
                    <button type="submit" disabled={createMutation.isPending}
                      className="px-6 py-3 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2 disabled:opacity-70 disabled:cursor-not-allowed hover:scale-105"
                      style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)', border: '1px solid rgba(96, 165, 250, 0.4)', color: '#e2e8f0' }}>
                      {createMutation.isPending ? <><i className="fa-solid fa-spinner fa-spin"></i><span>Creating...</span></> : <><i className="fa-solid fa-plus"></i><span>Create Template</span></>}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(8px)' }}
          onClick={(e) => e.target === e.currentTarget && setShowViewModal(false)}>
          <div className="rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" style={modalBg}>
            <div className="sticky top-0 px-8 py-6 flex items-center justify-between" style={{ ...modalBg, borderBottom: '1px solid rgba(100, 180, 255, 0.1)' }}>
              <div>
                <h3 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>{selectedTemplate.display_name}</h3>
                <p className="text-sm mt-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>{selectedTemplate.name}</p>
              </div>
              <button onClick={() => setShowViewModal(false)} style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                <i className="fa-solid fa-times text-2xl"></i>
              </button>
            </div>

            <div className="px-8 py-6">
              <div className="flex items-center space-x-4 mb-6 flex-wrap gap-2">
                {selectedTemplate.file_type === 'docx' ? (
                  <span className="px-2.5 py-1 text-xs font-medium rounded-full inline-flex items-center"
                    style={{ background: 'rgba(59, 130, 246, 0.15)', color: '#60a5fa', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                    <i className="fa-solid fa-file-word mr-1"></i>Word Template
                  </span>
                ) : (
                  <span className="px-2.5 py-1 text-xs font-medium rounded-full inline-flex items-center"
                    style={{ background: 'rgba(148, 163, 184, 0.15)', color: '#94a3b8', border: '1px solid rgba(148, 163, 184, 0.3)' }}>
                    <i className="fa-solid fa-file-code mr-1"></i>Markdown
                  </span>
                )}
                <span className="px-2.5 py-1 text-xs font-medium rounded-full uppercase"
                  style={{
                    background: selectedTemplate.use_case === 'aidw' ? 'rgba(251, 191, 36, 0.15)' :
                               selectedTemplate.use_case === 'caf' ? 'rgba(59, 130, 246, 0.15)' :
                               selectedTemplate.use_case === 'waf' ? 'rgba(52, 211, 153, 0.15)' :
                               'rgba(148, 163, 184, 0.15)',
                    color: selectedTemplate.use_case === 'aidw' ? '#fbbf24' :
                           selectedTemplate.use_case === 'caf' ? '#60a5fa' :
                           selectedTemplate.use_case === 'waf' ? '#34d399' :
                           '#94a3b8',
                    border: `1px solid ${selectedTemplate.use_case === 'aidw' ? 'rgba(251, 191, 36, 0.3)' :
                                        selectedTemplate.use_case === 'caf' ? 'rgba(59, 130, 246, 0.3)' :
                                        selectedTemplate.use_case === 'waf' ? 'rgba(52, 211, 153, 0.3)' :
                                        'rgba(148, 163, 184, 0.3)'}`
                  }}>
                  {selectedTemplate.use_case}
                </span>
                <span className="px-2.5 py-1 text-xs font-medium rounded-full"
                  style={{ background: 'rgba(139, 92, 246, 0.15)', color: '#a78bfa', border: '1px solid rgba(139, 92, 246, 0.3)' }}>
                  {selectedTemplate.category}
                </span>
                {selectedTemplate.is_active ? (
                  <span className="px-2.5 py-1 text-xs font-medium rounded-full"
                    style={{ background: 'rgba(52, 211, 153, 0.15)', color: '#34d399', border: '1px solid rgba(52, 211, 153, 0.3)' }}>Active</span>
                ) : (
                  <span className="px-2.5 py-1 text-xs font-medium rounded-full"
                    style={{ background: 'rgba(148, 163, 184, 0.15)', color: '#94a3b8', border: '1px solid rgba(148, 163, 184, 0.3)' }}>Inactive</span>
                )}
              </div>

              {selectedTemplate.description && <p className="mb-6" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>{selectedTemplate.description}</p>}

              {selectedTemplate.file_type === 'docx' ? (
                <div className="space-y-4">
                  {previewLoading ? (
                    <div className="rounded-xl p-12 text-center" style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
                      <i className="fa-solid fa-spinner fa-spin text-4xl mb-4" style={{ color: '#60a5fa' }}></i>
                      <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Loading preview...</p>
                    </div>
                  ) : previewBlob ? (
                    <DocxPreview blob={previewBlob} className="max-h-[60vh] overflow-auto" />
                  ) : (
                    <div className="rounded-xl p-6 text-center" style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
                      <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
                        style={{ background: 'rgba(59, 130, 246, 0.15)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                        <i className="fa-solid fa-file-word text-3xl" style={{ color: '#60a5fa' }}></i>
                      </div>
                      <h4 className="text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>Word Document Template</h4>
                      <p className="text-sm mb-4" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                        Preview unavailable. Download to view the document.
                      </p>
                    </div>
                  )}
                  <div className="flex items-center justify-between rounded-lg p-3" style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
                    <div className="flex items-center space-x-3">
                      <i className="fa-solid fa-file-word" style={{ color: '#60a5fa' }}></i>
                      <span className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                        {selectedTemplate.file_size > 0 && `${(selectedTemplate.file_size / 1024).toFixed(1)} KB`}
                      </span>
                    </div>
                    <button onClick={() => downloadTemplate(selectedTemplate.id, `${selectedTemplate.name}.docx`)}
                      className="px-4 py-2 rounded-lg text-sm font-medium transition hover:scale-105 inline-flex items-center space-x-2"
                      style={{ background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.4)', color: '#60a5fa' }}>
                      <i className="fa-solid fa-download"></i><span>Download</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="rounded-xl p-6" style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
                  <h4 className="text-sm font-semibold mb-3" style={{ color: '#e2e8f0' }}>Template Content</h4>
                  <pre className="text-sm whitespace-pre-wrap font-mono overflow-x-auto" style={{ color: 'rgba(148, 163, 184, 0.9)' }}>
                    {selectedTemplate.content}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(8px)' }}
          onClick={(e) => e.target === e.currentTarget && setShowEditModal(false)}>
          <div className="rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" style={modalBg}>
            <div className="sticky top-0 px-8 py-6 flex items-center justify-between" style={{ ...modalBg, borderBottom: '1px solid rgba(100, 180, 255, 0.1)' }}>
              <div>
                <h3 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>Edit Template</h3>
                <p className="text-sm mt-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>{selectedTemplate.name}</p>
              </div>
              <button onClick={() => setShowEditModal(false)} style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                <i className="fa-solid fa-times text-2xl"></i>
              </button>
            </div>

            <div className="px-8 py-6">
              <form onSubmit={handleUpdateSubmit}>
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>Display Name <span style={{ color: '#f87171' }}>*</span></label>
                    <input type="text" required value={selectedTemplate.display_name}
                      onChange={(e) => setSelectedTemplate({ ...selectedTemplate, display_name: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl text-sm"
                      style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }} />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>Use Case</label>
                      <select value={selectedTemplate.use_case} onChange={(e) => setSelectedTemplate({ ...selectedTemplate, use_case: e.target.value as UseCase })}
                        className="w-full px-4 py-3 rounded-xl text-sm"
                        style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }}>
                        {['Frameworks', 'Technology', 'Document Types', 'Services', 'Other'].map((group) => (
                          <optgroup key={group} label={group}>
                            {USE_CASE_OPTIONS.filter((o) => o.group === group).map((option) => (
                              <option key={option.value} value={option.value}>{option.label}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>Category</label>
                      <select value={selectedTemplate.category} onChange={(e) => setSelectedTemplate({ ...selectedTemplate, category: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl text-sm"
                        style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }}>
                        <option value="general">General</option>
                        <option value="workshop">Workshop</option>
                        <option value="assessment">Assessment</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>Description</label>
                    <input type="text" value={selectedTemplate.description}
                      onChange={(e) => setSelectedTemplate({ ...selectedTemplate, description: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl text-sm"
                      style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }} />
                  </div>

                  <div className="flex items-center space-x-2">
                    <input type="checkbox" id="is_active" checked={selectedTemplate.is_active}
                      onChange={(e) => setSelectedTemplate({ ...selectedTemplate, is_active: e.target.checked })}
                      className="rounded" style={{ background: 'rgba(30, 41, 59, 0.5)', borderColor: 'rgba(100, 180, 255, 0.3)' }} />
                    <label htmlFor="is_active" className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                      Template is active and available for document generation
                    </label>
                  </div>

                  {selectedTemplate.file_type === 'docx' ? (
                    <div className="rounded-xl p-6 text-center" style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3"
                        style={{ background: 'rgba(59, 130, 246, 0.15)', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
                        <i className="fa-solid fa-file-word text-2xl" style={{ color: '#60a5fa' }}></i>
                      </div>
                      <h4 className="text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>Word Document Template</h4>
                      <p className="text-sm mb-3" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                        To update the template file, delete this template and upload a new version.
                      </p>
                      <button type="button" onClick={() => downloadTemplate(selectedTemplate.id, `${selectedTemplate.name}.docx`)}
                        className="px-4 py-2 rounded-lg text-sm font-medium transition hover:scale-105 inline-flex items-center space-x-2"
                        style={{ background: 'rgba(59, 130, 246, 0.2)', border: '1px solid rgba(59, 130, 246, 0.4)', color: '#60a5fa' }}>
                        <i className="fa-solid fa-download"></i><span>Download Current Template</span>
                      </button>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-semibold mb-2" style={{ color: '#e2e8f0' }}>Template Content (Markdown) <span style={{ color: '#f87171' }}>*</span></label>
                      <textarea rows={16} required value={selectedTemplate.content}
                        onChange={(e) => setSelectedTemplate({ ...selectedTemplate, content: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl text-sm font-mono resize-none"
                        style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }}></textarea>
                    </div>
                  )}
                </div>

                <div className="flex items-center justify-end space-x-3 mt-8 pt-6" style={{ borderTop: '1px solid rgba(100, 180, 255, 0.1)' }}>
                  <button type="button" onClick={() => setShowEditModal(false)}
                    className="px-6 py-3 rounded-xl text-sm font-medium transition"
                    style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: 'rgba(148, 163, 184, 0.9)' }}>
                    Cancel
                  </button>
                  <button type="submit" disabled={updateMutation.isPending}
                    className="px-6 py-3 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2 disabled:opacity-70 disabled:cursor-not-allowed hover:scale-105"
                    style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)', border: '1px solid rgba(96, 165, 250, 0.4)', color: '#e2e8f0' }}>
                    {updateMutation.isPending ? <><i className="fa-solid fa-spinner fa-spin"></i><span>Saving...</span></> : <><i className="fa-solid fa-save"></i><span>Save Changes</span></>}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* AI-ify Modal */}
      {showAiifyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(8px)' }}
          onClick={(e) => e.target === e.currentTarget && !aiifyProcessing && setShowAiifyModal(false)}>
          <div className="rounded-2xl max-w-lg w-full" style={modalBg}>
            <div className="px-8 py-6 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(100, 180, 255, 0.1)' }}>
              <div>
                <h3 className="text-2xl font-bold flex items-center space-x-2" style={{ color: '#e2e8f0' }}>
                  <i className="fa-solid fa-wand-magic-sparkles" style={{ color: '#a78bfa' }}></i>
                  <span>AI-ify Template</span>
                </h3>
                <p className="text-sm mt-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Convert a human template to AI prompts</p>
              </div>
              <button onClick={() => !aiifyProcessing && setShowAiifyModal(false)} disabled={aiifyProcessing} style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                <i className="fa-solid fa-times text-2xl"></i>
              </button>
            </div>

            <div className="px-8 py-6">
              <div className="rounded-xl p-4 mb-6" style={{ background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.2)' }}>
                <div className="flex space-x-3">
                  <i className="fa-solid fa-magic mt-0.5" style={{ color: '#a78bfa' }}></i>
                  <div>
                    <h4 className="text-sm font-semibold mb-1" style={{ color: '#a78bfa' }}>How it works</h4>
                    <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                      Upload a Word document with human-written content. The AI will analyze each section
                      and generate <code className="px-1 rounded" style={{ background: 'rgba(139, 92, 246, 0.2)' }}>[[prompt]]</code> instructions.
                    </p>
                  </div>
                </div>
              </div>

              {aiifyProcessing ? (
                <div className="text-center py-8">
                  <i className="fa-solid fa-spinner fa-spin text-4xl mb-4" style={{ color: '#a78bfa' }}></i>
                  <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                    {aiifyJob?.status === 'pending' ? 'Starting conversion...' :
                     aiifyJob?.status === 'processing' ? 'Converting template to AI prompts...' :
                     'Processing...'}
                  </p>
                  {aiifyJob && aiifyJob.total_sections > 0 && (
                    <div className="mt-4">
                      <div className="w-full rounded-full h-2 mb-2" style={{ background: 'rgba(139, 92, 246, 0.2)' }}>
                        <div
                          className="h-2 rounded-full transition-all duration-500"
                          style={{
                            width: `${aiifyJob.progress_percent}%`,
                            background: 'linear-gradient(90deg, #8b5cf6, #a78bfa)'
                          }}
                        />
                      </div>
                      <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                        {aiifyJob.processed_sections} / {aiifyJob.total_sections} sections ({aiifyJob.progress_percent}%)
                      </p>
                    </div>
                  )}
                  <p className="text-sm mt-4" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                    You can close this dialog and check back later
                  </p>
                  <button
                    onClick={handleCancelAiify}
                    className="mt-4 px-4 py-2 rounded-lg text-sm transition hover:scale-105"
                    style={{ background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#f87171' }}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="rounded-xl p-8 text-center transition"
                  style={{ border: '2px dashed rgba(139, 92, 246, 0.3)', background: 'transparent' }}>
                  <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
                    style={{ background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.2)' }}>
                    <i className="fa-solid fa-file-word text-3xl" style={{ color: 'rgba(167, 139, 250, 0.5)' }}></i>
                  </div>
                  <p className="mb-2" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Upload your human-readable template</p>
                  <p className="text-sm mb-4" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Word documents with heading styles work best</p>
                  <label className="px-4 py-2 rounded-lg text-sm font-medium cursor-pointer transition hover:scale-105 inline-flex items-center space-x-2"
                    style={{ background: 'rgba(139, 92, 246, 0.2)', border: '1px solid rgba(139, 92, 246, 0.4)', color: '#a78bfa' }}>
                    <i className="fa-solid fa-upload"></i>
                    <span>Choose File</span>
                    <input type="file" accept=".docx,.dotx" className="hidden"
                      onChange={(e) => e.target.files?.[0] && handleAiifyUpload(e.target.files[0])} />
                  </label>
                </div>
              )}
            </div>

            <div className="px-8 py-4 rounded-b-2xl" style={{ background: 'rgba(30, 41, 59, 0.5)', borderTop: '1px solid rgba(100, 180, 255, 0.1)' }}>
              <p className="text-xs text-center" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                The converted template will download automatically. You can then upload it as a new AI template.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
